import { computed } from 'vue'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DRAWER_CONTEXT_SALES_PLAY_MEMBER } from '../../drawer/drawerTypes'
import { useHotkeys, type Hotkey } from './useHotkeys'

interface DrawerMember {
  membership_id: string
  kind: string
  account_id?: string | null
  contact_id?: string | null
}

/**
 * Drawer wiring + j/k row navigation shared by every candidate/membership
 * table. `playIdFor` is a resolver rather than a fixed value because Engaged
 * spans plays (each row carries its own `sales_play_id`) while the per-play
 * candidate pages have one fixed id for every row.
 */
export function useCandidateDrawerNav<T extends DrawerMember>(opts: {
  items: () => T[]
  playIdFor: (m: T) => string
  idAfter: (membershipId: string) => Promise<string | null>
  idBefore: (membershipId: string) => Promise<string | null>
  toggleSelect: (m: T, event?: Event) => void
  toggleSelectAll: () => void
}) {
  const { setContext: setDrawerContext, openDrawer, drawerContext } = useContextDrawer()

  const selectedMembershipId = computed(() => {
    const ctx: any = drawerContext.value
    if (ctx && ctx.type === DRAWER_CONTEXT_SALES_PLAY_MEMBER) return String(ctx.membershipId || '')
    return null
  })

  function openDrawerFor(m: T) {
    setDrawerContext({
      type: DRAWER_CONTEXT_SALES_PLAY_MEMBER,
      playId: opts.playIdFor(m),
      membershipId: m.membership_id,
      kind: m.kind,
      accountId: m.account_id || undefined,
      contactId: m.contact_id || undefined,
    } as any)
    openDrawer()
  }

  function scrollMemberIntoView(membershipId: string) {
    const el = document.querySelector(`[data-membership-id="${membershipId}"]`)
    if (el instanceof HTMLElement) el.scrollIntoView({ block: 'nearest' })
  }

  /**
   * Walks the ordered page, crossing into the next/previous page at the edges so
   * paging and keyboard nav behave as one system rather than stopping at row 50.
   */
  async function step(direction: 'next' | 'prev') {
    const current = selectedMembershipId.value
    if (!current) {
      const items = opts.items()
      if (items.length) openDrawerFor(items[0])
      return
    }
    const id = direction === 'next' ? await opts.idAfter(current) : await opts.idBefore(current)
    if (!id) return
    const row = opts.items().find((m) => String(m.membership_id) === id)
    if (row) {
      openDrawerFor(row)
      scrollMemberIntoView(id)
    }
  }

  /** Re-points the open drawer at the freshly-loaded copy of the same row after a reload/poll. */
  function syncDrawerContext() {
    const ctx: any = drawerContext.value
    if (!ctx || ctx.type !== DRAWER_CONTEXT_SALES_PLAY_MEMBER) return
    const current = opts.items().find((m) => m.membership_id === ctx.membershipId)
    if (!current) return
    const next: any = {
      type: DRAWER_CONTEXT_SALES_PLAY_MEMBER,
      playId: opts.playIdFor(current),
      membershipId: current.membership_id,
      kind: current.kind,
      accountId: current.account_id || undefined,
      contactId: current.contact_id || undefined,
    }
    if (
      next.accountId !== ctx.accountId ||
      next.contactId !== ctx.contactId ||
      next.kind !== ctx.kind
    ) {
      setDrawerContext(next)
    }
  }

  function clearDrawerIfOwned() {
    const ctx: any = drawerContext.value
    if (ctx && ctx.type === DRAWER_CONTEXT_SALES_PLAY_MEMBER) {
      setDrawerContext(null)
    }
  }

  const hotkeys: Hotkey[] = [
    { key: 'j', handler: () => { void step('next') }, description: 'next', primary: true, group: 'Navigate' },
    { key: 'k', handler: () => { void step('prev') }, description: 'previous', primary: true, group: 'Navigate' },
    { key: 'arrowdown', handler: (e) => { e.preventDefault(); void step('next') }, description: 'next', group: 'Navigate' },
    { key: 'arrowup', handler: (e) => { e.preventDefault(); void step('prev') }, description: 'previous', group: 'Navigate' },
    {
      key: 'x',
      handler: () => {
        const row = opts.items().find((m) => String(m.membership_id) === selectedMembershipId.value)
        if (row) opts.toggleSelect(row, new Event('change'))
      },
      description: 'select',
      primary: true,
      group: 'Select',
    },
    {
      key: 'mod+shift+a',
      handler: (e) => { e.preventDefault(); opts.toggleSelectAll() },
      description: 'select page',
      group: 'Select',
    },
  ]

  useHotkeys(hotkeys)

  return {
    drawerContext,
    selectedMembershipId,
    openDrawerFor,
    scrollMemberIntoView,
    step,
    syncDrawerContext,
    clearDrawerIfOwned,
    hotkeys,
  }
}
